import { useState } from "react";
import { InfoBlock } from "../InfoBlock";
import {
  ActionError,
  DataError,
  resource,
  type DeliverySectionContentProps
} from "./shared";

export function stripeConnectActionKey(started: boolean): "stripe.connectButton" | "stripe.retryButton" {
  return started ? "stripe.retryButton" : "stripe.connectButton";
}

export function simulatedStripeConnectPath(
  connectUrl: string,
  currentOrigin: string
): string | null {
  try {
    const origin = new URL(currentOrigin).origin;
    const parsed = new URL(connectUrl, origin);
    const query = [...parsed.searchParams.entries()];
    if (
      parsed.origin !== origin
      || parsed.pathname !== "/api/test/stripe-connect"
      || query.length !== 1
      || parsed.searchParams.get("test") !== "1"
    ) return null;
    return "/api/test/stripe-connect?test=1";
  } catch {
    return null;
  }
}

// The Stripe connection is its own rail step (Pavel: materials and payments
// are two very different things; they open together, materials come first).
export function StripeSection(props: DeliverySectionContentProps) {
  const [connectStarted, setConnectStarted] = useState(false);
  const stripe = resource(props.resources, "stripe_setup")?.value;
  const connected = props.milestones.some((item) => item.key === "stripe_receiving");
  // Meeting 24.7. (D9): the client connects their OWN Stripe through the
  // connect link Alex generates and the partner pastes into the console.
  // Nobody ever joins the client's Stripe team - that path was rejected.
  const rawConnectUrl = typeof stripe?.connect_url === "string" ? stripe.connect_url : null;
  const connectUrl = rawConnectUrl && /^https:\/\//.test(rawConnectUrl)
    ? rawConnectUrl
    : rawConnectUrl && props.testMode
      ? simulatedStripeConnectPath(
        rawConnectUrl,
        typeof window === "undefined" ? "http://localhost" : window.location.origin
      )
      : null;
  return (
    <section className="m5b-view" aria-labelledby="stripe-title">
      <header className="m5b-heading">
        <p className="eyebrow blue-eyebrow">{props.t("stripe.eyebrow")}</p>
        <h1 id="stripe-title">{props.t("rail.steps.stripe")}</h1>
        <p className="lead">{props.t("stripe.copy")}</p>
      </header>

      {(props.dataErrors?.has("resources") || props.dataErrors?.has("milestones")) && <DataError t={props.t} />}
      <InfoBlock
        name="stripe"
        className="stripe-card"
        eyebrow={props.t("stripe.inviteEyebrow")}
        title={props.t("stripe.inviteTitle")}
        body={<p>{props.t(connected
          ? "stripe.doneCopy"
          : connectUrl
            ? "stripe.statusWaiting"
            : "stripe.statusPreparingLink")}</p>}
        meta={(
          <span className={`status-pill ${connected ? "pill-done" : connectUrl ? "pill-client" : "pill-team"}`}>
            {props.t(connected ? "stripe.done" : connectUrl ? "stripe.waiting" : "stripe.preparing")}
          </span>
        )}
      >
        {!connected && (connectUrl ? (
          <div className="stripe-connect">
            <a
              className="primary-button"
              href={connectUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => setConnectStarted(true)}
            >
              {props.t(stripeConnectActionKey(connectStarted))}
            </a>
            <small>{props.t("stripe.connectNote")}</small>
          </div>
        ) : null)}
        {(props.partner?.displayName || props.partner?.calendlyUrl) && (
          <p className="stripe-contact-note">
            {props.partner?.calendlyUrl ? (
              <a className="secondary-button compact-button" href={props.partner.calendlyUrl} target="_blank" rel="noreferrer">
                {props.t("stripe.partnerCall")}
              </a>
            ) : props.t("stripe.partnerCall")}
          </p>
        )}
      </InfoBlock>
      <ActionError t={props.t} issue={props.actionError} />
    </section>
  );
}
